import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toastSuccess } from '@components/Toast';

import ConfirmationModal from '@features/crm/components/ConfirmationModal';
import {
	allFilterPages,
	arkworkListFields,
	generateArtworkFilterParams
} from '@features/filter/static/filterUtil';
import FilterComponent from '@features/filter/v2/FilterComponent';
import AddArtworkModal from '@features/assets/components/artwork/AddArtworkModal';
import ArtworkItem from '@features/assets/components/artwork/ArtworkItem';

import { clearFilterResponse, handleFilter, removeItemFromFilterResponse } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { deleteArtwork, getArtworkResponse } from '@services/assetsService';
import { fetchDependentFilterList } from '@services/commonService';

import { isPageReachBottom, printApiError } from '@utils/Util';

import { useGetArtworksQuery } from '@/features/assets/api/assetsSlice';
import LoaderComponent from '@/components/LoaderComponent';

const ArtworkList = ({
	isShowFilter,
	setTotalElements,
	showAddModal,
	setShowAddModal,
	setArtworkSummaryCount
}) => {
	const [isEditArtwork, setIsEditArtwork] = useState(false);
	const [artworkList, setArtworkList] = useState(null);
	const [selectedArtworkId, setSelectedArtworkId] = useState(null);
	const [selectedIdToDelete, setSelectedIdToDelete] = useState(null);
	const [artworkResponse, setArtworkResponse] = useState(null);
	const [page, setPage] = useState(0);
	let filterfields = useSelector((store) => store.filterFieldStore);
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const sortValue = filterfields['sort'] && filterfields['sort'][0]?.key;
	const { ...filterParams } = generateArtworkFilterParams(filterfields);
	const dispatch = useDispatch();
	const scrollRef = useRef({});

	const {
		data: artworks,
		isLoading: isArtworksLoading,
		refetch
	} = useGetArtworksQuery({
		params: {
			...generateArtworkFilterParams(filterfields),
			sort: sortValue || 'id,desc',
			page
		}
	});

	const setCurrentState = (data) => {
		scrollRef.current = data;
	};

	useEffect(() => {
		if (artworks) {
			setCurrentState(artworks);
			setArtworkList((prev) => {
				if (page === 0) {
					return artworks;
				}
				const prevIds = new Set((prev?.data || []).map((item) => item.id));
				const newData = (artworks.data || []).filter((item) => !prevIds.has(item.id));
				return {
					...artworks,
					data: [...(prev?.data || []), ...newData]
				};
			});
			setTotalElements(artworks.totalElements);
			setArtworkSummaryCount(artworks?.summaryData);
		}
	}, [artworks, page]);

	useEffect(() => {
		window.scrollTo(0, 0);
		dispatch(openLoader());

		dispatch(fetchDependentFilterList({ type: 'ARTWORK_LIST' }, filterParams));
		setPage(0);
		let queryParams = generateArtworkFilterParams(filterfields, 0, sortValue);
		dispatch(handleFilter('getAllArtWork', queryParams, false)).finally(() => {
			dispatch(closeLoader());
		});
	}, [filterfields]);

	const onActionSucces = () => {
		dispatch(fetchDependentFilterList({ type: 'ARTWORK_LIST' }, filterParams));
	};

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			dispatch(clearFilterResponse());
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { totalElements, currentPage, data } = scrollRef.current;
			if (totalElements !== data?.length) {
				setPage(() => currentPage + 1);
			}
		}
	};

	const onEdit = async (id) => {
		setShowAddModal(true);
		setSelectedArtworkId(id);
		setIsEditArtwork(true);
	};

	const onCloseModal = () => {
		setShowAddModal(false);
		setSelectedArtworkId(null);
		setArtworkResponse(null);
		setIsEditArtwork(false);
	};

	const fetchArtworkData = async () => {
		if (selectedArtworkId) {
			try {
				dispatch(openLoader());
				const data = await getArtworkResponse(selectedArtworkId);
				setArtworkResponse(data);
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	const onCloseConfirmationModal = () => {
		setOpenConfirmation(false);
	};

	const OnDeleteArtwork = async () => {
		dispatch(openLoader());
		try {
			await deleteArtwork(selectedIdToDelete);
			dispatch(removeItemFromFilterResponse(selectedIdToDelete));
			refetch();
			dispatch(closeLoader());
			refetch();
			setOpenConfirmation(false);
			toastSuccess('Successfully deleted');
		} catch (error) {
			dispatch(closeLoader());
			printApiError(error);
		}
	};

	const onDeleteClick = (id) => {
		setSelectedIdToDelete(id);
		setOpenConfirmation(true);
	};

	useEffect(() => {
		fetchArtworkData();
	}, [selectedArtworkId]);
	const renderArtworkList = () => {
		if (artworkList?.data?.length > 0) {
			return artworkList.data.map((artwork, index) => {
				return (
					<ArtworkItem
						artwork={artwork}
						key={`artwork_item_${index}`}
						onEdit={onEdit}
						onDeleteClick={onDeleteClick}
						fileUrl={
							artwork?.documentResponseList?.find(
								(doc) => doc.docType === 'ARTWORK_ILLUSTRATOR_FILE'
							)?.docUrl
						}
					/>
				);
			});
		}
	};

	return (
		<LoaderComponent loading={isArtworksLoading}>
			<section className="filter-data-container filter-v3-data-container under-inner-tabs bg-white">
				<FilterComponent
					filterPageName={allFilterPages.ARTWORK_LIST}
					fields={arkworkListFields}
					showFilter={isShowFilter}
					isDependentFilter={true}
				/>
				<div className="data-table">
					<div className="design-list-container">
						<div className="design-list-row">{renderArtworkList()}</div>
					</div>
				</div>
			</section>

			{showAddModal && (
				<AddArtworkModal
					artWorkResponse={artworkResponse}
					onCloseModal={onCloseModal}
					isEdit={isEditArtwork}
					artworkId={selectedArtworkId}
					onActionSucces={onActionSucces}
				/>
			)}

			{openConfirmation && (
				<ConfirmationModal
					title={'Delete artwork'}
					closeModal={onCloseConfirmationModal}
					description={' about deleting this artwork?'}
					callback={OnDeleteArtwork}
					footerType={'CLOSE'}
				/>
			)}
		</LoaderComponent>
	);
};

export default ArtworkList;
